/**
 * REST notification endpoints — see docs/CONTRACTS.md §46.
 *
 *   POST   /notifications/tokens   — register a new push token (FCM / APNs / web)
 *   DELETE /notifications/tokens   — unregister an existing token (logout / device wipe)
 *
 * Plus the on-wire shape of a push message — used by the middleware
 * NotificationsService and (later) by the mobile + web push consumers.
 */
import { z } from 'zod';
import { UuidSchema } from '../common/id.js';
import { Iso8601Schema } from '../common/timestamp.js';
// ── Platform ────────────────────────────────────────────────────────────────
export const PushPlatformSchema = z.enum(['android', 'ios', 'web']);
// ── POST /notifications/tokens ──────────────────────────────────────────────
export const PushTokenRegisterBody = z.object({
    token: z.string().min(8).max(4096),
    platform: PushPlatformSchema,
    appVersion: z.string().max(32).optional(),
});
export const PushTokenRegisterResponse = z.object({
    id: UuidSchema,
    registeredAt: Iso8601Schema,
});
// ── DELETE /notifications/tokens ────────────────────────────────────────────
export const PushTokenDeleteBody = z.object({
    token: z.string().min(8).max(4096),
});
// ── Notification kinds — every wireable event ───────────────────────────────
export const NotificationKindSchema = z.enum([
    // To Main User / Sub Main User / Member (mobile)
    'device.offline',
    'device.online',
    'device.activated',
    'device.ownership_transferred',
    'device.removed_by_admin',
    'device.ota_available',
    'device.ota_completed',
    'device.remote_action_completed',
    /** The lift's observed alarm bell started ringing (OFF→ON edge). Sent to
     *  the device owner + active members — an alarm nobody sees is useless. */
    'device.lift_alarm',
    'member.added',
    'member.removed',
    'warranty.created',
    'warranty.state_changed',
    'warranty.expiring',
    'service_request.state_changed',
    // To Seller (web push — stretch)
    'seller.device_registered',
    'seller.service_request_created',
    'seller.device_offline_24h',
    // To Admin / Super Admin (web push — stretch)
    'admin.warranty_opened',
    'admin.rollout_completed',
    'admin.remote_action_failed',
    // To Installer (mobile)
    'installer.lift_authorized',
]);
// ── Wire payload (provider-agnostic) ────────────────────────────────────────
export const NotificationPayloadSchema = z.object({
    kind: NotificationKindSchema,
    title: z.string().min(1).max(120),
    body: z.string().min(1).max(500),
    /** Free-form data delivered as FCM data-only fields (string-coerced by client). */
    data: z.record(z.unknown()).default({}),
});
//# sourceMappingURL=notifications.js.map